import React from "react";
import { GridEdge, GridNode, HoverElement } from "../../types";
import { computeLineRent, lmpLookup } from "../../powerFlow/transmissionRent";
import KeyIcon from "./KeyIcon";
import ColumnHeader from "./ColumnHeader";

interface Props {
  lines: GridEdge[];
  nodes: GridNode[];
  onEdgeRatingChange: (edgeId: string, rating: number) => void;
  onCommitEdit?: () => void;
  hoverElement: HoverElement | null;
  onElementHover: (element: HoverElement | null) => void;
}

function LineTable({ lines, nodes, onEdgeRatingChange, onCommitEdit, hoverElement, onElementHover }: Props) {
  const lmpByNodeId = lmpLookup(nodes);
  const rents = lines.map((line) => computeLineRent(line, lmpByNodeId));
  const totalRent = rents.reduce<number>((sum, r) => sum + (r ?? 0), 0);
  const anyRent = rents.some((r) => r !== undefined);
  const tableStyle = {
    width: "100%",
    borderCollapse: "collapse" as const,
    fontSize: "12px",
    marginTop: "10px",
  };

  const thStyle = {
    background: "#f5f5f5",
    padding: "8px",
    textAlign: "left" as const,
    borderBottom: "1px solid #ddd",
    fontWeight: "bold" as const,
  };

  const tdStyle = {
    padding: "6px 8px",
    borderBottom: "1px solid #eee",
  };

  if (lines.length === 0) {
    return (
      <p style={{ padding: "20px", textAlign: "center", color: "#666" }}>
        No power lines in the grid
      </p>
    );
  }

  return (
    <table style={tableStyle}>
      <thead>
        <tr>
          <ColumnHeader column="id" style={thStyle}>ID<KeyIcon /></ColumnHeader>
          <ColumnHeader column="from" style={thStyle}>From</ColumnHeader>
          <ColumnHeader column="to" style={thStyle}>To</ColumnHeader>
          <th style={thStyle}>Flow (MW)</th>
          <ColumnHeader column="rating" style={thStyle}>Rating (MW)</ColumnHeader>
          <th style={thStyle}>Utilization (%)</th>
          <th style={thStyle} title="LMP at the 'to' bus minus LMP at the 'from' bus">
            ΔLMP ($/MWh)
          </th>
          <th style={thStyle} title="Congestion rent = flow × ΔLMP, in dollars per hour">
            Rent ($/h)
          </th>
          <th style={thStyle}>Resistance (Ω)</th>
        </tr>
      </thead>
      <tbody>
        {lines.map((line, i) => {
          const current = Math.abs(line.current || 0);
          const utilization = (current / line.rating) * 100;

          const lmpFrom = lmpByNodeId.get(line.from);
          const lmpTo = lmpByNodeId.get(line.to);
          const deltaLMP =
            lmpFrom !== undefined && lmpTo !== undefined ? lmpTo - lmpFrom : undefined;
          const rent = rents[i];

          const isHighlighted = hoverElement?.type === 'Edge' && hoverElement?.id === line.id;
          return (
            <tr 
              key={line.id}
              onMouseEnter={() => onElementHover({ type: 'Edge', id: line.id })}
              onMouseLeave={() => onElementHover(null)}
              style={{ backgroundColor: isHighlighted ? '#FFD70030' : 'transparent' }}
            >
              <td style={tdStyle}>{line.id}</td>
              <td style={tdStyle}>{line.from}</td>
              <td style={tdStyle}>{line.to}</td>
              <td style={tdStyle}>{current.toFixed(2)}</td>
              <td style={tdStyle}>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "8px" }}
                >
                  <input
                    type="range"
                    min="1"
                    max="500"
                    value={line.rating}
                    onChange={(e) =>
                      onEdgeRatingChange(line.id, parseFloat(e.target.value))
                    }
                    onMouseUp={onCommitEdit}
                    onKeyUp={onCommitEdit}
                    onBlur={onCommitEdit}
                    style={{
                      width: "80px",
                      height: "4px",
                      borderRadius: "2px",
                      background: "#ddd",
                      outline: "none",
                      cursor: "pointer",
                    }}
                  />
                  <span style={{ minWidth: "35px", fontSize: "11px" }}>
                    {line.rating.toFixed(1)}
                  </span>
                </div>
              </td>
              <td
                style={{
                  ...tdStyle,
                  color:
                    utilization > 100
                      ? "#F44336"
                      : utilization > 80
                      ? "#FF9800"
                      : "#4CAF50",
                  fontWeight: "bold",
                }}
              >
                {utilization.toFixed(1)}%
              </td>
              <td style={tdStyle}>
                {deltaLMP !== undefined ? `$${deltaLMP.toFixed(2)}` : "N/A"}
              </td>
              <td
                style={{
                  ...tdStyle,
                  color: rent !== undefined && rent >= 1 ? "#8E24AA" : "#666",
                  fontWeight: rent !== undefined && rent >= 1 ? "bold" : "normal",
                }}
                title="Congestion rent = flow × (LMP_to − LMP_from)"
              >
                {rent !== undefined
                  ? `$${rent.toLocaleString(undefined, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}`
                  : "N/A"}
              </td>
              <td style={tdStyle}>{line.resistance.toFixed(3)}</td>
            </tr>
          );
        })}
      </tbody>
      {anyRent && lines.length > 0 && (
        <tfoot>
          <tr>
            <td style={{ ...tdStyle, fontWeight: "bold" }} colSpan={6}>
              Total congestion rent
            </td>
            <td style={{ ...tdStyle, fontWeight: "bold", color: "#8E24AA" }}>
              {`$${totalRent.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}`}
            </td>
            <td style={tdStyle}></td>
          </tr>
        </tfoot>
      )}
    </table>
  );
}

export default LineTable;